import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useTheme } from "../../../../shared/common/ThemeContext";
import {
  Activity,
  Zap,
  Target,
  TrendingUp,
  Users,
  AlertTriangle,
  CheckCircle2,
  BarChart3,
  Gauge,
  Footprints,
  Timer,
  ArrowLeftRight,
  ArrowRight,
  User,
  X,
  GitCompare,
  Play,
  Pause,
  Heart,
  ArrowUpCircle,
  ArrowDownCircle,
  TrendingDown,
  Loader2,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
  Cell,
  ReferenceLine,
} from "recharts";
import TrainerTeamPerformanceCharts from "../overview/TrainerTeamPerformanceCharts"
import { apiListRuns, apiGetRunResults, apiGetRunInputs, apiGetCalendarMicrocycle, apiGetMaquetteDemo } from "../../pages/predictiveApi";
import { fetchAuth } from "../../../../shared/common/api";
import { STORAGE_PREFIX } from "@config";

const MAQUETTE_CLUB_ID = "7c61d7a5-7da7-409a-9bfd-18416640f640";
const isMaquetteClub = () => {
  const cid = localStorage.getItem(`${STORAGE_PREFIX}club_id`);
  return cid === MAQUETTE_CLUB_ID;
};

// Import from results folder
import {
  MUSCLES,
  POSITION_CONFIG,
  POSITION_GROUPS,
  POSITION_GROUP_COLORS,
  TrainabilityBadge,
  RiskBadge,
  ProgressBar,
  MuscleTrainabilityBar,
  MetricTooltip,
  ScenarioSelector,
  CompactMetricsBar,
  PlayerCard,
  PositionGroup,
  PlayerDetailPanel,
  ScenarioComparisonModal,
  MicrocycleTimeline,
  PerformanceCharts,
  TeamComparisonCharts,
} from "./results";

// ================== MAIN COMPONENT ==================

export default function ResultsInsightsTab() {
  const { isDarkMode } = useTheme();
  const muted = isDarkMode ? "text-gray-500" : "text-gray-400";

  const [runs, setRuns] = useState([]);
  const [runsData, setRunsData] = useState({});
  const [runsInputs, setRunsInputs] = useState({});
  const [calendarByRun, setCalendarByRun] = useState({});
  const [weeklyHistoryByRun, setWeeklyHistoryByRun] = useState({});
  const [activeScenario, setActiveScenario] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPlayerPanel, setShowPlayerPanel] = useState(false);

  const handleSelectPlayer = (p) => {
    setSelectedPlayer(p);
    setShowPlayerPanel(true);
  };

  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

const toScore10 = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;

  // ancien format 0-1
  if (n >= 0 && n <= 1.5) return clamp(n * 10, 0, 10);

  // format percent (0-100)
  if (n > 10 && n <= 100) return clamp(n / 10, 0, 10);

  // cas bug: 6500 => probablement percent *100
  if (n > 100 && n <= 10000) return clamp(n / 1000, 0, 10);

  // format déjà /10
  return clamp(n, 0, 10);
};

const getTrainabilityDisplay = (score10, trainability_level) => {
  const v = Number(score10);
  const status = trainability_level?.global; // "underload" | "overload" | null

  // ✅ cas spécial : score bas + overload/underload
  if (Number.isFinite(v) && v < 4) {
    if (status === "overload") {
      return { tone: "rose", label: "too high" };
    }
    if (status === "underload") {
      return { tone: "indigo", label: "too low" };
    }
    // si status absent -> sub-optimal normal
    return { tone: "sky", label: "sub-optimal" };
  }

  // normal
  if (!Number.isFinite(v)) return { tone: "slate", label: "n/a" };
  if (v >= 7) return { tone: "emerald", label: "optimal" };
  if (v >= 4) return { tone: "amber", label: "average" };
  return { tone: "sky", label: "sub-optimal" };
};

  const summaryStats = useMemo(() => {
  const data = runsData[activeScenario];
  return data?.microcycle_summary || null;
}, [runsData, activeScenario]);

const summaryStats10 = useMemo(() => {
  if (!summaryStats) return null;
  return {
    ...summaryStats,
    avg_team_trainability_10: toScore10(summaryStats.avg_team_trainability || 0),
  };
}, [summaryStats, toScore10]);


  useEffect(() => {
    const fetchRuns = async () => {
      setLoading(true);
      setError(null);
      try {
        // --- Maquette club: force-load demo scenarios ---
        if (isMaquetteClub()) {
          const demo = await apiGetMaquetteDemo();
          const demoRuns = demo?.runs || [];
          const demoResults = demo?.results || {};
          setRuns(demoRuns);
          setRunsData(demoResults);
          if (demoRuns.length) setActiveScenario(demoRuns[0].id);
          else setActiveScenario(null);
          setLoading(false);
          return;
        }
        // --- Normal flow ---
        const list = await apiListRuns();
        const rawRuns = Array.isArray(list) ? list : [];
        // Deduplicate by scenario name: keep only the first (most recent) run per name
        const seen = new Set();
        const uniqueRuns = rawRuns.filter(r => {
          const key = (r.name || "").trim().toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setRuns(uniqueRuns);
        if (uniqueRuns.length) setActiveScenario(uniqueRuns[0].id);
        else setActiveScenario(null);
      } catch (err) {
        
        setError("Failed to load simulation runs");
      } finally {
        setLoading(false);
      }
    };

    fetchRuns();
  }, []);

  

  useEffect(() => {
  if (!activeScenario) return;
    if (runsData[activeScenario]) return;

    (async () => {
      try {
        const data = await apiGetRunResults(activeScenario);
        setRunsData(prev => ({ ...prev, [activeScenario]: data }));
      } catch (e) {
       
      }
    })();
  }, [activeScenario, runsData]);


  
  useEffect(() => {
    if (!activeScenario) return;
    if (runsInputs[activeScenario]) return;
    if (isMaquetteClub()) return; // skip for maquette demo (no real runs in DB)

    (async () => {
      try {
        const inputs = await apiGetRunInputs(activeScenario);
        setRunsInputs(prev => ({ ...prev, [activeScenario]: inputs }));
      } catch (e) {
        
      }
    })();
  }, [activeScenario, runsInputs]);

  
  useEffect(() => {
    if (!activeScenario) return;
    const inputs = runsInputs[activeScenario];
    if (!inputs) return;
    if (calendarByRun[activeScenario]) return;

    const ctx = inputs?.microcycle?.calendar_context || {};
    const season = ctx.season;
    const from = ctx.from || ctx.start;
    if (!season) return;

    (async () => {
      try {
        const cal = await apiGetCalendarMicrocycle({ season, from });
        setCalendarByRun(prev => ({ ...prev, [activeScenario]: cal }));
      } catch (e) {
        
      }
    })();
  }, [activeScenario, runsInputs, calendarByRun]);

  const dayTypeFromSlots = (slots) => {
    const all = [
      ...(slots?.morning || []),
      ...(slots?.afternoon || []),
    ];

    // slots peuvent être legacy string => sécurise
    const types = all
      .map(e => (typeof e === "string" ? e : e?.type))
      .filter(Boolean);

    if (types.includes("match")) return "match";
    if (types.includes("recovery")) return "recovery";
    if (types.includes("training")) return "training";
    if (types.includes("rest")) return "rest";

    // fallback si on ne sait pas
    return types.length ? "training" : "rest";
  };

  const currentScenario = useMemo(() => {
    const run = runs.find(r => r.id === activeScenario);
    const data = runsData[activeScenario];
    if (!run || !data) return null;

    const cal = calendarByRun[activeScenario];
    const days = cal?.days?.map(d => ({
      label: d.md,            // "MD-3", "MD", "PS"
      date: d.date,
      type: dayTypeFromSlots(d.slots),
    })) || [];

    return {
      key: run.id,
      name: run.name || `Scenario ${run.variant || "A"}`,
      description: "",
      status: run.status,
      days,
      results: data,
    };
  }, [runs, activeScenario, runsData, calendarByRun]);

const playerResults = useMemo(() => {
  const data = runsData[activeScenario];
  if (!data?.players) return [];

  const weeklyHistoryData = weeklyHistoryByRun[activeScenario] || {};

  return Object.values(data.players).map(pr => {
    const avg10 = toScore10(pr.summary?.avg_trainability ?? pr.summary?.readiness_score ?? 0);

    return {
      id: pr.player_id,
      first_name: pr.first_name,
      last_name: pr.last_name,
      position: pr.position,
      jersey_number: pr.jersey_number,
      profile_picture: pr.profile_picture,
      sessions: pr.sessions,
      weeklyHistory: weeklyHistoryData[String(pr.player_id)] || [], // Ajout des données weeklyHistory

      ...pr.summary,

      avg_trainability_10: Number(avg10.toFixed(1)),

      trainability_level:
        pr.summary?.trainability_level ??
        pr.trainability_level ??
        null,
    };
  });
}, [runsData, activeScenario, weeklyHistoryByRun]);



useEffect(() => {
  if (!playerResults?.length) return;
  const p = playerResults[0];

}, [playerResults]);


useEffect(() => {
  if (!summaryStats10) return;
}, [summaryStats10]);


  // Scenario selector data
  const scenarios = useMemo(() => {
    return runs.map(r => ({
      key: r.id,
      name: r.name || `Scenario ${r.variant || ""}`.trim(),
      status: r.status
    }));
  }, [runs]);

  // Get scenario comparison
  const getScenarioComparison = useCallback(() => {
    return runs.map(run => {
      const data = runsData[run.id];
      const summary = data?.microcycle_summary || {};
      return {
        key: run.id,
        name: run.name || `Scenario ${run.variant || ""}`.trim(),
        avgTrainability: Number(toScore10(summary.avg_team_trainability || 0).toFixed(1)),
        playersAtRisk: summary.players_at_risk || 0,
        optimalPlayers: summary.optimal_readiness_count || 0,
        totalLoad: summary.total_training_load || 0,
      };
    });
  }, [runs, runsData]);

  const teamChartsDate = useMemo(() => {
    // 0) fallback absolu: today (YYYY-MM-DD)
    const todayIso = new Date().toISOString().slice(0, 10);

    // 1) si ton calendar microcycle fournit une date exploitable, prends-la
    const d = currentScenario?.days?.find(x => x?.date)?.date;
    if (d) return d;

    // 2) sinon tente calendar_context si dispo
    const ctx = runsInputs?.[activeScenario]?.microcycle?.calendar_context;
    const from = ctx?.from || ctx?.start;
    if (from) return from;

    // 3) sinon: today
    return todayIso;
  }, [currentScenario, runsInputs, activeScenario]);

  const microcycleWindow = useMemo(() => {
    // Utiliser le calendrier du run
    const cal = calendarByRun[activeScenario];
    const days = cal?.days || [];
    const dates = days.map(d => d?.date).filter(Boolean);

    if (!dates.length) return null;

    const sorted = [...dates].sort();
    const firstDate = sorted[0];
    
    // Calculer 7 jours avant le début du microcycle
    const startDate = new Date(firstDate);
    startDate.setDate(startDate.getDate() - 7);
    const startIso = startDate.toISOString().split('T')[0];
    
    return { start: startIso, end: sorted[sorted.length - 1] };
  }, [calendarByRun, activeScenario]);

  // Charger weeklyHistory pour le scénario actif
  useEffect(() => {
    if (!activeScenario || !microcycleWindow) return;
    if (weeklyHistoryByRun[activeScenario]) return; // Déjà chargé

    const fetchWeeklyHistory = async () => {
      try {
        const qs = new URLSearchParams({
          start_date: microcycleWindow.start,
          end_date: microcycleWindow.end,
          predictive_run_id: activeScenario
        });
        
        const response = await fetchAuth(
          `/trainer/players_weekly_history_predictive?${qs.toString()}`
        );
        
        if (!response.ok) throw new Error("Failed to load weekly history");
        
        const data = await response.json();
        
        // Index par player_id pour faciliter la fusion
        // L'API renvoie "id" (pas "player_id"), on indexe par les deux pour robustesse
        const historyByPlayer = {};
        data.forEach(player => {
          const pid = String(player.player_id || player.id || "");
          if (pid) historyByPlayer[pid] = player.weeklyHistory || [];
        });
        
        setWeeklyHistoryByRun(prev => ({
          ...prev,
          [activeScenario]: historyByPlayer
        }));
      } catch (err) {
       
      }
    };

    fetchWeeklyHistory();
  }, [activeScenario, microcycleWindow, weeklyHistoryByRun]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 size={40} className="mx-auto mb-3 animate-spin text-indigo-500" />
          <p className={`text-sm ${muted}`}>Loading simulation results...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle size={40} className="mx-auto mb-3 text-rose-500" />
          <p className="text-sm text-rose-500">{error}</p>
        </div>
      </div>
    );
  }

  // No data state
  if (runs.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <BarChart3 size={40} className={`mx-auto mb-3 ${muted}`} />
          <p className={`text-sm ${muted}`}>No completed simulation runs yet.</p>
          <p className={`text-xs mt-1 ${muted}`}>Run a microcycle simulation to see results here.</p>
        </div>
      </div>
    );
  }

  
  return (
    <div className="space-y-4">

      {activeScenario && (
        <div className="space-y-4">

          {/* Header: Title + Scenario selector + Metrics - clean inline */}
          <div className="flex flex-col gap-3">
            {/* Top bar */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold">Simulation Results</h2>
                <ScenarioSelector
                  scenarios={scenarios}
                  activeKey={activeScenario}
                  onSelect={setActiveScenario}
                  isDarkMode={isDarkMode}
                />
              </div>
              <div className="flex items-center gap-2 sm:ml-auto">
                <button
                  onClick={() => setShowCompareModal(true)}
                  className={`
                    flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
                    ${isDarkMode 
                      ? "bg-indigo-600 hover:bg-indigo-500 text-white" 
                      : "bg-indigo-500 hover:bg-indigo-600 text-white"
                    }
                  `}
                >
                  <GitCompare size={14} />
                  Compare
                </button>
              </div>
            </div>

            {/* Metrics inline */}
          <CompactMetricsBar stats={summaryStats10} isDarkMode={isDarkMode} />
          </div>

          {/* Main content grid - no cards/boxes */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Player grid - left side */}
            <div className="xl:col-span-2 space-y-4">
              {/* Microcycle Timeline - above players */}
              <MicrocycleTimeline scenario={currentScenario} isDarkMode={isDarkMode} />

              {/* Section title with sort controls */}
              <div className="flex items-center justify-between">
                <span className={`text-xs font-medium uppercase tracking-wide ${muted}`}>
                  Players ({playerResults.length})
                </span>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${muted}`}>Sort:</span>
                  <button
                    onClick={() => setSortOrder(prev => prev === "asc" ? "desc" : "asc")}
                    className={`
                      flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all
                      ${isDarkMode 
                        ? "bg-white/5 hover:bg-white/10 text-gray-300" 
                        : "bg-gray-100 hover:bg-gray-200 text-gray-600"}
                    `}
                  >
                    <span>Trainability</span>
                    {sortOrder === "asc" ? (
                      <ArrowUpCircle size={14} className="text-emerald-500" />
                    ) : (
                      <ArrowDownCircle size={14} className="text-rose-500" />
                    )}
                  </button>
                </div>
              </div>

              {/* Player cards grouped by position */}
              <div className="space-y-4">
                {(() => {
                  // Group players by position
                  const grouped = playerResults.reduce((acc, player) => {
                    const pos = player.position || "OTHER";
                    if (!acc[pos]) acc[pos] = [];
                    acc[pos].push(player);
                    return acc;
                  }, {});

                  // Sort each group by trainability
                  Object.keys(grouped).forEach(pos => {
                    grouped[pos].sort((a, b) => {
                    const diff = (a.avg_trainability_10 || 0) - (b.avg_trainability_10 || 0);
                      return sortOrder === "asc" ? diff : -diff;
                    });
                  });

                  // Sort positions by order
                  const sortedPositions = Object.keys(grouped).sort((a, b) => {
                    const orderA = POSITION_CONFIG[a]?.order || 99;
                    const orderB = POSITION_CONFIG[b]?.order || 99;
                    return orderA - orderB;
                  });

                  return sortedPositions.map(pos => (
                    <PositionGroup
                      key={pos}
                      title={POSITION_CONFIG[pos]?.label || pos}
                      players={grouped[pos]}
                      selectedPlayer={selectedPlayer}
                      onSelectPlayer={handleSelectPlayer}
                      isDarkMode={isDarkMode}
                      color={POSITION_CONFIG[pos]?.color}
                    />
                  ));
                })()}
              </div>
            </div>

            <div className="xl:col-span-1">
              {showPlayerPanel && selectedPlayer ? (
                <div className={`sticky top-4 rounded-2xl p-4 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-50/80"}`}>
                  <PlayerDetailPanel player={selectedPlayer} isDarkMode={isDarkMode} />

                  <PerformanceCharts player={selectedPlayer} isDarkMode={isDarkMode} />
                </div>
              ) : null}
            </div>
          </div>

          {/* Team Comparison Section - Full width below players */}
          <TeamComparisonCharts
            playerResults={playerResults}
            teamAvg10={toScore10(summaryStats?.avg_team_trainability || 0)}
            isDarkMode={isDarkMode}
          />


          {/* Comparison Modal */}
          <ScenarioComparisonModal
            isOpen={showCompareModal}
            onClose={() => setShowCompareModal(false)}
            isDarkMode={isDarkMode}
            scenarios={scenarios}
            getScenarioComparison={getScenarioComparison}
          />

          {/* Team Trainability Evolution */}
          <div className="space-y-3 mt-8">
            <h3 className="text-lg font-bold">Team Trainability Evolution</h3>

            {microcycleWindow ? (
              <TrainerTeamPerformanceCharts
                predictiveRunId={activeScenario}
                startDate={microcycleWindow.start}
                endDate={microcycleWindow.end}
                embedded
              />
            ) : (
              <div className={`flex flex-col items-center justify-center py-12 px-4 rounded-lg ${
                isDarkMode ? "bg-gray-800/30 border border-gray-700" : "bg-gray-100 border border-gray-200"
              }`}>
                <svg className="w-12 h-12 mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-sm opacity-70 text-center">
                  Unable to determine the time window for this scenario.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
